import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

export default function SideModal({
  show,
  toggle,
  title,
  children,
  containerClass,
  headerClassName,
  closeIconClassName,
  showHeader = true,
  onOpened,
}: {
  show: boolean;
  toggle: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  containerClass?: string;
  headerClassName?: string;
  closeIconClassName?: string;
  showHeader?: boolean;
  // Fires once the panel has finished its open width transition (i.e. it is at
  // its final width). Useful for content that must measure the final layout on
  // mount — e.g. an embedded PDF viewer that fits/persists zoom to the width
  // available when it loads.
  onOpened?: () => void;
}) {
  const ASIDE_TRANSITION_MS = 300;

  const [isRendered, setIsRendered] = useState(show);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const showRef = useRef(show);
  showRef.current = show;

  // Keep the latest onOpened in a ref so the fallback timer doesn't need it as a
  // dependency (and won't fire a stale callback).
  const onOpenedRef = useRef(onOpened);
  onOpenedRef.current = onOpened;
  // Ensure onOpened fires at most once per open cycle, regardless of whether the
  // width transitionend event or the timeout fallback gets there first. Reset on close.
  const hasFiredOpenedRef = useRef(false);

  const fireOpened = () => {
    if (hasFiredOpenedRef.current) return;
    if (!showRef.current) return;
    // Only fire once the panel has actually reached its open width. The width
    // transition doesn't start until the double rAF below flips isOpen (and thus
    // w-0 → w-[50vw]); those rAFs can be delayed under main-thread load or in a
    // background tab, so a stray timer/rAF must not report "opened" while at w-0.
    if (!isOpenRef.current) return;
    hasFiredOpenedRef.current = true;
    onOpenedRef.current?.();
  };

  useEffect(() => {
    if (show) {
      setIsRendered(true);
      hasFiredOpenedRef.current = false;
      let innerId: number;
      const outerId = requestAnimationFrame(() => {
        innerId = requestAnimationFrame(() => setIsOpen(true));
      });
      return () => {
        cancelAnimationFrame(outerId);
        cancelAnimationFrame(innerId);
      };
    }
    setIsOpen(false);
    hasFiredOpenedRef.current = false;
    // If we close before the open rAFs run, aside stays at w-0 — no width transition, so
    // onTransitionEnd never fires. Unmount immediately when the panel was never opened.
    if (!isOpenRef.current) {
      setIsRendered(false);
      return;
    }
    // If isOpen was true, closing relies on width transition + onTransitionEnd. If that event
    // is missed (browser quirk, no actual width change, etc.), the backdrop would stick; clear
    // after the CSS transition duration while still closed.
    const fallbackMs = ASIDE_TRANSITION_MS + 50;
    const timer = window.setTimeout(() => {
      if (!showRef.current) {
        setIsRendered(false);
      }
    }, fallbackMs);
    return () => clearTimeout(timer);
  }, [show]);

  // onOpened relies on the width transitionend event. If that event is missed
  // (browser quirk, no actual width change, etc.), consumers waiting on it (e.g.
  // FAQs' panelReady gating the PDF viewer) would stay stuck. Fire a fallback after
  // the CSS transition duration — but measured from when the transition actually
  // begins (isOpen flips true), not from when `show` did. Timing it from `show`
  // could elapse before the double rAF starts the transition, firing while still
  // at w-0. fireOpened is idempotent with the transitionend event and guards on
  // isOpen, so a late run is a no-op.
  useEffect(() => {
    if (!isOpen) return;
    const openFallbackMs = ASIDE_TRANSITION_MS + 50;
    const openTimer = window.setTimeout(fireOpened, openFallbackMs);
    return () => clearTimeout(openTimer);
  }, [isOpen]);

  useEffect(() => {
    if (!isRendered) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isRendered]);

  const handleBackdropClick = () => {
    toggle();
  };

  const handleAsideTransitionEnd = (e: React.TransitionEvent<HTMLElement>) => {
    if (e.propertyName !== "width") return;
    if (!isOpen) {
      setIsRendered(false);
    } else {
      fireOpened();
    }
  };

  if (!isRendered) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[999] bg-[#56555554]"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <main className="flex h-full justify-end">
        <aside
          className={twMerge("h-screen shrink-0 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.25)] overflow-hidden bg-gray-50 transition-[width] duration-300 ease-out", isOpen ? "w-[50vw]" : "w-0")}
          onClick={(e) => e.stopPropagation()}
          onTransitionEnd={handleAsideTransitionEnd}
        >
          <div
            className={twMerge("relative h-full min-w-0 w-full py-7 px-9", containerClass)}
          >
            {showHeader && (
              <header
                className={twMerge(
                  "flex justify-between font-semibold",
                  headerClassName
                )}
              >
                <h2 className="min-w-0 flex-1 text-black">{title}</h2>
                <button type="button" onClick={toggle} aria-label="Close">
                  <CloseIcon className={twMerge("h-6 w-6 text-black", closeIconClassName)} />
                </button>
              </header>
            )}
            {children}
          </div>
        </aside>
      </main>
    </div>
  );
}
